<template>
  <div class="form-container">
    <h1 class="page-title">業務項目の編集</h1>
    <form @submit.prevent="updateAnnualSchedule" @keydown.enter.prevent>
      <div v-if="annualScheduleData.execution_months_pending" class="alert alert-warning">
        実施月未設定です。月別の請求金額と作業予定を設定して保存すると、未設定状態が解除されます。
      </div>
      <div v-if="showDetailLockNotice" class="alert alert-info">
        管理者以外は、請求済みの月は編集できません。
      </div>

      <div class="mb-3">
        <label for="m_customer_id" class="form-label">顧客：</label>
        <span class="multiline-text">{{ building.m_customer?.customer_nm }}</span>
      </div>

      <div class="mb-3">
        <label for="m_bulding_id" class="form-label">建物：</label>
        {{ building.building_nm }}
      </div>

      <div class="mb-3">
        <label class="form-label">業務区分 <span class="badge text-bg-danger">必須</span></label>
        <select class="form-select w-half" v-model="annualScheduleData.m_business_classification_id" required>
          <option value="">選択してください</option>
          <option v-for="businessClassification in businessClassifications" :key="businessClassification.id" :value="businessClassification.id">{{ businessClassification.business_classification_nm }}</option>
        </select>
      </div>

      <div class="mb-3">
        <label for="work_content" class="form-label">業務内容 <span class="badge text-bg-danger">必須</span></label>
        <input v-model="annualScheduleData.work_content" id="work_content" type="text" placeholder="業務内容" class="form-control w-half" required>
        <div v-if="errorMessages.report?.work_content" class="alert alert-danger mt-2">{{ errorMessages.report?.work_content[0] }}</div>
      </div>

      <div class="mb-3">
        <label for="work_unit_price" class="form-label">業務単価</label>
        <div class="d-flex align-items-center">
          <CommaNumberInput
            v-model="annualScheduleData.work_unit_price"
            id="work_unit_price"
            placeholder="業務単価"
            class="form-control w-half"
            @error="val => workUnitPriceHasError = val"
          />
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">
          社内担当者（建物マスタに登録される社内担当者）<br>
          <span class="text-danger">（※下記の詳細データで自動的に反映されます）</span>
        </label>
        <input
          class="form-control w-half"
          type="text"
          :value="annualScheduleData.user?.name || ''"
          readonly
        />
        <input
          type="hidden"
          name="user_id"
          :value="annualScheduleData.user_id || ''"
        />
      </div>

      <!-- 協力会社の有無 チェックボックス -->
      <div class="mb-3">
        <label for="vendor_flg" class="ECM_CheckboxInput">
          <input
            v-model="annualScheduleData.is_partner_company"
            :true-value="true"
            :false-value="false"
            id="vendor_flg"
            type="checkbox"
            class="ECM_CheckboxInput-Input"
          >
          <span class="ECM_CheckboxInput-DummyInput"></span>
          <h4 class="ms-3">協力会社の有無</h4>
        </label>
      </div>

      <div v-if="annualScheduleData.is_partner_company === true">
        <div class="mb-3">
          <label class="form-label">協力会社<span class="text-danger">（※下記の詳細データで自動的に反映されます）</span></label>
          <div v-for="(vendorId, index) in annualScheduleData.m_vendor_ids" :key="index" class="d-flex align-items-center mb-2">
            <VendorSelectBox
              v-model="annualScheduleData.m_vendor_ids[index]"
              :options="vendors"
            />
            <button type="button" class="btn btn-danger ms-2 text-nowrap" @click="removeVendor(index)">削除</button>
          </div>
          <div class="btn-area mt-2 w-half">
            <button type="button" class="btn btn-info mt-2" @click="addVendor">追加</button>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">含めたい業務項目の選択</label>
        <select class="form-select w-half" v-model="annualScheduleData.t_building_work_annual_schedule_id">
          <option value="">選択してください</option>
          <option v-for="schedule in schedules" :key="schedule.id" :value="schedule.id">{{ schedule.work_content }}</option>
        </select>
      </div>

      <div class="mb-3">
        <label for="remarks" class="form-label">備考</label>
        <textarea v-model="annualScheduleData.remarks" id="remarks" class="form-control"></textarea>
        <div v-if="errorMessages.report?.remarks" class="alert alert-danger mt-2">{{ errorMessages.report?.remarks[0] }}</div>
      </div>

      <h2 class="mt-4">月別の詳細データ</h2>
      <p class="text-muted mb-2">※管理者以外は、請求済みの月は編集できません。契約終了後の月も編集できません。</p>
      <table class="table table-bordered monthly-detail-data">
        <tr>
          <th class="text-nowrap month-data-header month-width">月</th>
          <td class="text-nowrap month-data-header">請求金額</td>
          <td class="text-nowrap month-data-header">作業予定の有無</td>
          <td class="text-nowrap month-data-header">社内担当者</td>
          <td class="text-nowrap month-data-header" v-if="annualScheduleData.is_partner_company === true">協力会社</td>
          <td class="text-nowrap month-data-header">備考</td>
        </tr>
        <tr
          v-for="monthOffset in 12"
          :key="monthOffset"
          :class="{
            'bg-light-gray': isMonthGrayOut(monthOffset)
          }"
        >
          <th class="month-data-left month-width">
            {{ displayMonth(monthOffset) }}
            <div class="detail-btn-area">
              <button
                type="button"
                class="btn btn-sm btn-secondary"
                @click="clearMonthData(displayMonthKey(monthOffset))"
                :disabled="isMonthReadOnly(monthOffset)"
              >
                クリア
              </button>
            </div>
          </th>
          <td class="align-top p-0">
            <div class="d-flex align-items-center">
              <CommaNumberInput
                v-model="scheduleDetails[displayMonthKey(monthOffset)].bill_amount"
                :disabled="isMonthReadOnly(monthOffset)"
                placeholder="請求金額"
                class="form-control"
                @input="handleBillAmountInput(monthOffset)"
              />
            </div>
          </td>
          <td class="align-top">
            <select
              class="form-select"
              v-model="scheduleDetails[displayMonthKey(monthOffset)].is_planned"
              :disabled="isMonthReadOnly(monthOffset)"
            >
              <option value="true">◯</option>
              <option value="false">×</option>
            </select>
          </td>
          <td class="align-top">
            <select class="form-select" v-model="scheduleDetails[displayMonthKey(monthOffset)].user_id" :disabled="isMonthReadOnly(monthOffset)">
              <option value="">選択してください</option>
              <option v-for="user in users" :key="user.id" :value="user.id">{{ user.name }}</option>
            </select>
          </td>
          <td v-if="annualScheduleData.is_partner_company === true" class="align-top p-0">
            <div v-for="(vendor, index) in scheduleDetails[displayMonthKey(monthOffset)].vendors" :key="index" class="d-flex align-items-center">
              <VendorSelectBox
                v-model="vendor.m_vendor_id"
                :options="vendors"
                :disabled="isMonthReadOnly(monthOffset)"
              />
              <div class="d-flex align-items-center">
                <CommaNumberInput
                  v-model="vendor.request_amount"
                  class="form-control ms-2"
                  :disabled="isMonthReadOnly(monthOffset)"
                />
              </div>
              <button
                type="button"
                class="btn btn-sm btn-danger ms-2"
                @click="removeMonthVendor(monthOffset, index)"
                :disabled="isMonthReadOnly(monthOffset)"
              >
                削除
              </button>
            </div>
            <div>
              <button
                type="button"
                class="btn btn-sm btn-info mt-2"
                @click="addMonthVendor(monthOffset)"
                :disabled="isMonthReadOnly(monthOffset)"
              >
                追加
              </button>
            </div>
          </td>
          <td class="align-top">
            <textarea
              v-model="scheduleDetails[displayMonthKey(monthOffset)].remarks"
              class="form-control"
              rows="2"
              :disabled="isMonthReadOnly(monthOffset)"
            ></textarea>
          </td>
        </tr>
      </table>

      <div class="btn-area">
        <a class="btn btn-secondary mx-1" @click="$router.back()">戻る</a>
        <button type="submit" class="btn btn-warning mx-1">更新</button>
      </div>

    </form>
  </div>
</template>

<script>
import { ref, onMounted, computed, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useRoute } from 'vue-router';
import {
  getSelectBoxUsers,
  getSelectBoxVendors,
  getBusinessClassifications,
  getBuildingAnnualSchedules,
  getBuildingAnnualSchedule,
  getBuilding,
  updateAnnualScheduleWithDetails
} from '@/api/axios';
import VendorSelectBox from '@/components/searchSelectBoxes/VendorSelectBox.vue';
import CommaNumberInput from '@/components/shared/CommaNumberInput.vue';

export default {
  components: {
    VendorSelectBox,
    CommaNumberInput
  },
  setup() {
    const title = ref('業務項目編集');

    watchEffect(() => {
      document.title = title.value;
    });

    const userId = ref('');
    const userName = ref('');
    const users = ref([]);
    const building = ref({});
    const vendors = ref([]);
    const businessClassifications = ref([]);
    const schedules = ref([]);

    const errorMessages = ref({}); // エラーメッセージ用のリアクティブプロパティ

    const store = useStore();
    const router = useRouter();
    const route = useRoute();
    const userRole = computed(() => store.state.userRole ?? localStorage.getItem('userRole'));
    const isAdmin = computed(() => ['0', '999', 0, 999].includes(userRole.value));
    const scheduleDetailId = route.params.id;
    const buildingId = ref(route.params.building_id);
    const year = ref(new Date(route.params.start_month).getFullYear());
    const month = ref(new Date(route.params.start_month).getMonth() + 1);
    const startMonth = ref(new Date(route.params.start_month));
    const showDetailLockNotice = computed(() => !isAdmin.value);

    const scheduleDetails = ref({});
    const annualScheduleData = ref({
      m_customer_id: '',
      m_building_id: buildingId.value,
      m_business_classification_id: '',
      work_content: '',
      work_year: year.value,
      work_unit_price: '',
      is_partner_company: '',
      m_vendor_ids: [''],
      t_building_work_annual_schedule_id: '',
      remarks: '',
      user_id: '',
      sort_no: '',
      t_building_work_schedule_detail_vendors: [],
      t_building_work_schedule_details: []
    })

    // 12か月分のデータを初期化
    for (let monthOffset = 1; monthOffset <= 12; monthOffset++) {
      const monthKey = new Date(startMonth.value);
      monthKey.setMonth(startMonth.value.getMonth() + monthOffset - 1);
      const keyString = `${monthKey.getFullYear()}-${monthKey.getMonth() + 1}`; 
      scheduleDetails.value[keyString] = {
        work_year: monthKey.getFullYear(),
        work_month: monthKey.getMonth() + 1,
        bill_amount: '',
        is_planned: '',
        user_id: '',
        is_not_implemented: false,
        has_billing: false,
        locked_for_general_user: false,
        vendors: [],  // ← 協力会社情報を保持する配列を追加
        remarks: ''
      };
    }

    const getYearMonth = (monthOffset) => {
      const base = new Date(startMonth.value); // startMonth は開始月の Date
      base.setMonth(base.getMonth() + monthOffset - 1);
      return {
        year: base.getFullYear(),
        month: base.getMonth() + 1
      };
    };

    // 表示する月名の生成
    const displayMonth = (monthOffset) => {
      const date = new Date(startMonth.value);
      date.setMonth(startMonth.value.getMonth() + monthOffset - 1);
      return `${date.getFullYear()}年${date.getMonth() + 1}月`;
    };

    // scheduleDetailsのキー用
    const displayMonthKey = (monthOffset) => {
      const date = new Date(startMonth.value);
      date.setMonth(startMonth.value.getMonth() + monthOffset - 1);
      return `${date.getFullYear()}-${date.getMonth() + 1}`;
    };

    // 請求金額を入力したら作業予定が◯に、担当者がメインの担当者で指定した人の値が入るようにする
    const handleBillAmountInput = (monthOffset) => {
      // 年月を取得
      const { year: y, month: m } = getYearMonth(monthOffset);

      // ★ 契約終了後なら処理しない（内部の値も触らない）
      if (isMonthReadOnly(monthOffset) || isAfterContractEndYM(y, m)) {
        return;
      }

      const key = displayMonthKey(monthOffset);
      const detail = scheduleDetails.value[key];

      // 変更前の値が空 or null だったかを判定用に保存（初期状態とみなす）
      const wasEmpty = detail.bill_amount === '' || detail.bill_amount === null;

      // bill_amount が現在空でない（つまり入力された）なら
      if (!wasEmpty) {
        // 初回入力時のみセット（is_planned などがまだ false の場合）
        if (!detail.is_planned) {
          detail.is_planned = true;
          detail.user_id = annualScheduleData.value.user_id;

          // vendors が未定義 or 空ならセット
          if (!detail.vendors || detail.vendors.length === 0) {
            detail.vendors = annualScheduleData.value.m_vendor_ids.map(vendorId => ({
              m_vendor_id: vendorId,
              request_amount: ''
            }));
          }
        }
      }
    };

    const addVendor = () => {
      annualScheduleData.value.m_vendor_ids.push('');
    };

    const removeVendor = (index) => {
      annualScheduleData.value.m_vendor_ids.splice(index, 1);
    };

    const addMonthVendor = (monthOffset) => {
      const monthKey = displayMonthKey(monthOffset);

      // scheduleDetails がリアクティブであることを保証
      if (!scheduleDetails.value[monthKey]) {
        scheduleDetails.value[monthKey] = { vendors: [] };
      }

      if (!scheduleDetails.value[monthKey].vendors) {
        scheduleDetails.value[monthKey].vendors = [];
      }

      scheduleDetails.value[monthKey].vendors.push({ m_vendor_id: '', request_amount: '' });
    };

    const removeMonthVendor = (monthOffset, index) => {
      const monthKey = displayMonthKey(monthOffset);

      if (scheduleDetails.value[monthKey] && scheduleDetails.value[monthKey].vendors) {
        scheduleDetails.value[monthKey].vendors.splice(index, 1);
      }
    };

    // 協力会社の有無がオフになったとき、協力会社情報をリセットするwatch
    watch(
      () => annualScheduleData.value.is_partner_company,
      (isChecked) => {
        if (!isChecked) {
          // チェックが外れた時に協力会社と担当者のセレクトボックスの値をリセット
          // annualScheduleData.value.m_vendor_id = '';
        }
      }
    );

    // 各月の入力データをクリアする関数
    const clearMonthData = (displayMonthKeyOffset) => {
      if (isMonthReadOnlyByKey(displayMonthKeyOffset)) return;

      let target_schedule_detail = scheduleDetails.value[displayMonthKeyOffset]

      // その月のデータが存在しない場合は何もしない
      if (!scheduleDetails.value[displayMonthKeyOffset]) return;

      target_schedule_detail.bill_amount = null;  // 金額をクリア（null または 0）
      target_schedule_detail.is_planned = false;  // 予定の選択状態をクリア（null にすると未選択になる）
      target_schedule_detail.remarks = '';  // 備考欄を空にする
      target_schedule_detail.vendors = [];  // 協力会社の情報を空の配列にリセット（すべて削除）
    };

    const fetchWorkScheduleDatas = async () => {
      userId.value = localStorage.getItem('userId');
      userName.value = localStorage.getItem('userName');

      try {
        // 🔸 各候補データをまとめて取得
        const [
          usersRes,
          vendorsRes,
          businessClassificationsRes,
          buildingRes,
          annualSchedulesRes,
        ] = await Promise.all([
          getSelectBoxUsers(),
          getSelectBoxVendors(),
          getBusinessClassifications(),
          getBuilding(buildingId.value),
          getBuildingAnnualSchedules(buildingId.value, year.value, month.value)
        ])

        users.value = usersRes.data
        vendors.value = vendorsRes.data
        businessClassifications.value = businessClassificationsRes.data
        building.value = buildingRes.data
        schedules.value = annualSchedulesRes.data.annual_schedules

        // 🔸 全データが揃った後に main データを取得
        const tempRes = await getBuildingAnnualSchedule(scheduleDetailId)
        annualScheduleData.value = tempRes.data.annual_schedule

        // 🔸 vendor ID の配列もここで更新（順番重要！）
        if (annualScheduleData.value.t_building_work_schedule_vendors) {
          annualScheduleData.value.m_vendor_ids =
            annualScheduleData.value.t_building_work_schedule_vendors.map(v => v.m_vendor_id)
        }

        // 🔸 各月の詳細データ
        const workDetails = annualScheduleData.value.t_building_work_schedule_details
        workDetails.forEach(workDetail => {
          const key = `${workDetail.work_year}-${workDetail.work_month}`
          scheduleDetails.value[key] = {
            work_year: workDetail.work_year,
            work_month: workDetail.work_month,
            bill_amount: workDetail.bill_amount,
            is_planned: workDetail.is_planned,
            user_id: workDetail.user_id,
            is_not_implemented: workDetail.is_not_implemented,
            has_billing: !!workDetail.has_billing,
            locked_for_general_user: !!workDetail.locked_for_general_user,
            remarks: workDetail.remarks,
            vendors: workDetail.t_building_work_schedule_detail_vendors?.map(vendor => ({
              m_vendor_id: vendor.m_vendor_id,
              vendor_nm: vendor.m_vendor?.vendor_nm ?? '',
              request_amount: vendor.request_amount
            })) || []
          }
        })
      } catch (error) {
        console.error('業務項目または選択肢の取得に失敗しました:', error)
      }
    }

    // 契約終了年月の計算
    const contractEndYM = computed(() => {
      if (!building.value || !building.value.contract_end_dt) {
        return { year: null, month: null };
      }

      const d = new Date(building.value.contract_end_dt);
      return {
        year: d.getFullYear(),
        month: d.getMonth() + 1
      };
    });

    // 指定年月 (y,m) が契約終了より後か？
    const isAfterContractEndYM = (y, m) => {
      if (!contractEndYM.value.year) return false; // 契約終了が無いなら全部OK

      return (
        y > contractEndYM.value.year ||
        (y === contractEndYM.value.year && m > contractEndYM.value.month)
      );
    };

    const isMonthReadOnlyByKey = (monthKey) => {
      const detail = scheduleDetails.value[monthKey];
      if (!detail) return false;

      return isAfterContractEndYM(detail.work_year, detail.work_month) ||
        (!isAdmin.value && !!detail.locked_for_general_user);
    };

    const isMonthReadOnly = (monthOffset) => {
      const monthKey = displayMonthKey(monthOffset);
      return isMonthReadOnlyByKey(monthKey);
    };

    const isMonthGrayOut = (monthOffset) => {
      const monthKey = displayMonthKey(monthOffset);
      const detail = scheduleDetails.value[monthKey];
      if (!detail) return false;

      return !!detail.has_billing || isAfterContractEndYM(detail.work_year, detail.work_month);
    };

    const updateAnnualSchedule = async () => {
      try {
        // ★ startMonth から 12ヶ月分だけを抽出する（余計な翌年分は送らない）
        const filteredDetails = [];
        for (let monthOffset = 1; monthOffset <= 12; monthOffset++) {
          const key = displayMonthKey(monthOffset);
          filteredDetails.push(scheduleDetails.value[key]);
        }

        annualScheduleData.value.t_building_work_schedule_details = filteredDetails;

        // `t_building_work_schedule_detail_vendors` を作成
        annualScheduleData.value.t_building_work_schedule_detail_vendors = [];

        Object.entries(scheduleDetails.value).forEach(([monthKey, monthData]) => {
          if (monthKey.length > 0 && monthData.vendors.length > 0) {
            monthData.vendors.forEach((vendor) => {
              annualScheduleData.value.t_building_work_schedule_detail_vendors.push({
                work_year: monthData.work_year,
                work_month: monthData.work_month,
                m_vendor_id: vendor.m_vendor_id,
                request_amount: vendor.request_amount
              });
            });
          }
        });

        // API に送信
        await updateAnnualScheduleWithDetails(scheduleDetailId, annualScheduleData.value, scheduleDetails.value);
        
        store.dispatch('setFlashMessage', {message: '年間予定と作業予定詳細更新完了！', type: 'success'});
        router.push({
          path: `/annual-work-schedules/${buildingId.value}/${year.value}-${month.value}-${startMonth.value.getDate()}`,
          query: route.query
        });
      } catch (error) {
        console.error('更新中にエラーが発生しました:', error);
        const message = error?.response?.data?.error || '更新中にエラーが発生しました';
        store.dispatch('setFlashMessage', { message, type: 'danger' });
      }
    };

    onMounted(() => {
      fetchWorkScheduleDatas();
    });

    return {
      title,
      userId,
      userName,
      users,
      building,
      vendors,
      businessClassifications,
      schedules,
      getYearMonth,
      isAfterContractEndYM,
      isMonthReadOnly,
      isMonthGrayOut,
      displayMonth,
      displayMonthKey,
      clearMonthData,
      addMonthVendor,
      removeMonthVendor,
      addVendor,
      removeVendor,
      handleBillAmountInput,
      errorMessages,
      annualScheduleData,
      scheduleDetails,
      showDetailLockNotice,
      updateAnnualSchedule
    };
  }
};
</script>

<style scoped>
table {
  border-collapse: collapse;
  border-spacing: 0;
  width: 100%;
}
th, td {
  padding: 8px;
  vertical-align: middle;
  border: 1px solid #ccc !important;
  min-width: 100px;
  font-size: 14px;
  text-align: center;
}
input[type="text"],
input[type="email"],
input[type="password"]{
  width: 100%;
  padding: 10px;
  margin-bottom: 20px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 16px;
}

.w-half{
  width: 50% !important;
}

.w-third{
  width: 30% !important;
}

.btn-area{
  margin-top: 32px;
  text-align: center;
}

.btn-area-fixed{
  position: fixed;
  top: 100px;
  right: 60px;
  z-index: 50;
}

/* 月別の詳細データ */
.monthly-detail-data{
  margin: 20px auto 20px 0;
  width: 100%;
  font-size: 14px;
}
.monthly-detail-data .month-data-header{
  background-color: #003366;
  color: #fff;
}
.monthly-detail-data .month-data-left,
.monthly-detail-data .month-data-left .detail-btn-area{
  background-color: #f0f8ff;
}
.monthly-detail-data .month-width{
  width: 120px;
}
.monthly-detail-data input[type="number"],
.monthly-detail-data select,
.monthly-detail-data textarea{
  width: 100%;
  padding: 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  font-size: 14px;
}
</style>
